import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import moment from "moment";
import "daterangepicker/daterangepicker.css";
import "daterangepicker";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2/dist/js/select2.full.min.js";
import "./RaiseComplaint.css";
import "./AdminServiceRequestList.css";
import { complaintAPI, masterDataAPI, productMasterAPI, userAPI } from "../services/api";
import { approverDashboardAPI } from "../services/approverDashboardApi";
import { renderSrLinkHtml, renderStatusHtml } from "../utils/xss";
import { useSidebar } from "../contexts/SidebarContext";
import { hasUserType } from "../utils/auth";
import { getUser } from "../utils/auth";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import {
  saveAdminServiceRequestListState,
  readAdminServiceRequestListState,
  readAdminServiceRequestListRestoreSnapshot,
  markAdminServiceRequestListRestored,
  hasAdminServiceRequestListDrilldownUrl,
  ADMIN_SERVICE_REQUESTS_FROM_LIST,
} from "../utils/adminServiceRequestListState";

/** Dropdown label: show name only (strip " (Territory)" / " (Role)" suffix). */
function displayNameOnly(label) {
  const s = String(label ?? "").trim();
  const idx = s.indexOf(" (");
  return idx > 0 ? s.slice(0, idx).trim() : s;
}

const isIsoDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());

function parseBusinessVerticalCsvFromSearch(search) {
  const bvParam = new URLSearchParams(search || "").get("bv");
  if (!bvParam) return null;
  const codes = decodeURIComponent(bvParam)
    .split(",")
    .map((s) => String(s || "").trim())
    .filter(Boolean);
  return codes.length > 0 ? codes.join(",") : null;
}

function buildBusinessVerticalMatchContext(bvCsv, bvName, bvOptions = []) {
  const codes = new Set();
  String(bvCsv || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((c) => codes.add(c));

  const names = new Set();
  const nameKey = bvName ? String(bvName).trim().toLowerCase() : "";
  if (nameKey) names.add(nameKey);

  (Array.isArray(bvOptions) ? bvOptions : []).forEach((o) => {
    const id = String(o?.id ?? "").trim();
    const text = String(o?.text ?? "").trim();
    const textKey = text.toLowerCase();
    if (!id && !text) return;
    if (id && codes.has(id) && textKey) names.add(textKey);
    if (nameKey && textKey === nameKey) {
      if (id) codes.add(id);
      names.add(textKey);
    }
  });

  if (codes.size === 0 && bvName) {
    codes.add(String(bvName).trim());
  }

  return {
    codesCsv: codes.size > 0 ? [...codes].join(",") : null,
    names: [...names],
    exactNameMatch: Boolean(nameKey),
  };
}

/** Parse admin SR list drill-down query params (sync on mount / navigation). */
function parseAdminListUrlFilters(search, defaultFY) {
  const searchParams = new URLSearchParams(search || "");
  const statusParam = searchParams.get("status");
  const categoryParam = searchParams.get("category");
  const queryRaisedParam = searchParams.get("queryRaised");
  const settlementTypeParam = searchParams.get("settlementType");
  const fromDateParam = searchParams.get("fromDate");
  const toDateParam = searchParams.get("toDate");
  const repeatScopeFromParam =
    searchParams.get("repeatScopeFrom") ||
    searchParams.get("repeatComplaintsScopeFromDate");
  const repeatScopeToParam =
    searchParams.get("repeatScopeTo") ||
    searchParams.get("repeatComplaintsScopeToDate");
  const repeatComplaintParam = searchParams.get("repeatComplaint");
  const bvParam = searchParams.get("bv");
  const srNoParam = searchParams.get("srNo");
  const zoneNameParam = searchParams.get("zoneName") || searchParams.get("zone");
  const marketCodeParam = searchParams.get("marketCode");

  const onlyQueriesRaisedByMe =
    queryRaisedParam === "true" ||
    queryRaisedParam === "1" ||
    String(queryRaisedParam || "").toLowerCase() === "true";

  let statusCodeFromUrl = null;
  let statusCodesCsvFromUrl = null;
  let backendStatusForUrl = null;
  if (statusParam) {
    const parts = statusParam
      .split(",")
      .map((s) => decodeURIComponent(s.trim()))
      .filter(Boolean);
    if (parts.length > 0) {
      const mappedStatuses = parts
        .flatMap((v) => {
          const key = String(v || "").trim();
          if (!key) return [];
          if (key === "2") return ["Pending", "WIP"];
          if (key === "3") return ["Approved"];
          if (key === "4") return ["Rejected"];
          if (key === "5") return ["Closed"];
          if (key === "1") return ["Pending"];
          if (key === "7") return ["WIP"];
          return [key];
        })
        .filter(Boolean);
      backendStatusForUrl = mappedStatuses[0] || null;
      statusCodeFromUrl = mappedStatuses[0] || null;
      statusCodesCsvFromUrl = mappedStatuses.length > 0 ? mappedStatuses.join(",") : null;
    }
  }

  const fromDateDecoded = fromDateParam ? decodeURIComponent(fromDateParam).trim() : "";
  const toDateDecoded = toDateParam ? decodeURIComponent(toDateParam).trim() : "";
  const effectiveFrom = isIsoDate(fromDateDecoded) ? fromDateDecoded : defaultFY.start;
  const effectiveTo = isIsoDate(toDateDecoded) ? toDateDecoded : defaultFY.end;

  const decodedCategory = categoryParam ? decodeURIComponent(categoryParam).trim() : "";
  const decodedSettlementType = settlementTypeParam
    ? decodeURIComponent(settlementTypeParam).trim()
    : "";
  const decodedRepeatScopeFrom = repeatScopeFromParam
    ? decodeURIComponent(repeatScopeFromParam).trim()
    : "";
  const decodedRepeatScopeTo = repeatScopeToParam
    ? decodeURIComponent(repeatScopeToParam).trim()
    : "";
  const decodedSrNo = srNoParam ? decodeURIComponent(srNoParam).trim() : "";
  const decodedZoneName = zoneNameParam ? decodeURIComponent(zoneNameParam).trim() : "";
  const decodedMarketCode = marketCodeParam
    ? decodeURIComponent(marketCodeParam).trim()
    : "";

  const repeatComplaintsScopeFromDate = isIsoDate(decodedRepeatScopeFrom)
    ? decodedRepeatScopeFrom
    : null;
  const repeatComplaintsScopeToDate = isIsoDate(decodedRepeatScopeTo)
    ? decodedRepeatScopeTo
    : null;
  const repeatComplaintsDrilldown =
    repeatComplaintParam === "1" ||
    repeatComplaintParam === "true" ||
    String(repeatComplaintParam || "").toLowerCase() === "true" ||
    Boolean(repeatComplaintsScopeFromDate || repeatComplaintsScopeToDate);

  return {
    backendStatusForUrl,
    ui: {
      startDate: effectiveFrom,
      endDate: effectiveTo,
      categoryFilter: decodedCategory,
      srInvoiceFilter: decodedSrNo,
      productVerticalFilter: bvParam
        ? decodeURIComponent(bvParam).split(",").map((s) => s.trim()).filter(Boolean)
        : null,
      marketCode: decodedMarketCode || null,
    },
    appliedFilters: {
      createdBy: null,
      statusCode: statusCodeFromUrl,
      statusCodesCsv: statusCodesCsvFromUrl,
      businessVerticalCodesCsv: bvParam
        ? decodeURIComponent(bvParam)
        : null,
      marketCode: decodedMarketCode || null,
      srTypeCode: null,
      category: decodedCategory || null,
      settlementType: decodedSettlementType || null,
      fromDate: effectiveFrom,
      toDate: effectiveTo,
      repeatComplaintsScopeFromDate,
      repeatComplaintsScopeToDate,
      repeatComplaintsDrilldown,
      searchText: decodedSrNo || null,
      onlyQueriesRaisedByMe,
      zoneName: decodedZoneName || null,
    },
  };
}

/** Admin list loads Domestic (M001) by default when URL has no marketCode. */
const DEFAULT_ADMIN_LIST_MARKET = "M001";

function pickDefaultListMarket(options) {
  if (!Array.isArray(options) || !options.length) {
    return DEFAULT_ADMIN_LIST_MARKET;
  }
  const byCode = options.find(
    (o) =>
      String(o.marketCode || "").trim().toUpperCase() === DEFAULT_ADMIN_LIST_MARKET
  );
  if (byCode?.marketCode) return byCode.marketCode;
  const byName = options.find(
    (o) => String(o.marketName || "").trim().toLowerCase() === "domestic"
  );
  if (byName?.marketCode) return byName.marketCode;
  return options[0]?.marketCode || DEFAULT_ADMIN_LIST_MARKET;
}

/** Users eligible for delegate — journey workflow users whose step is still Pending. */
function mapJourneyRowsToDelegateUsers(journeyRows) {
  if (!Array.isArray(journeyRows)) return [];
  const seen = new Set();
  const users = [];
  const sorted = [...journeyRows].sort(
    (a, b) =>
      Number(a.levelSequence ?? a.LevelSequence ?? 0) -
      Number(b.levelSequence ?? b.LevelSequence ?? 0)
  );
  for (const row of sorted) {
    const id = String(row.userIdCode || row.UserIdCode || "").trim();
    if (!id) continue;

    const activityRaw = String(row.activity || row.Activity || "").trim();
    const activity = activityRaw.toLowerCase();
    if (activity !== "pending") continue;

    const isDone = row.isDone ?? row.IsDone;
    if (isDone === true || isDone === 1 || String(isDone).toLowerCase() === "true") {
      continue;
    }

    const key = id.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    users.push({
      userIdCode: id,
      fullName: String(row.fullName || row.FullName || id).trim(),
      roleName: String(
        row.userTypeCode ||
          row.UserTypeCode ||
          row.userTypeName ||
          row.UserTypeName ||
          ""
      ).trim(),
      levelSequence: row.levelSequence ?? row.LevelSequence ?? null,
      activity: activityRaw,
    });
  }
  return users;
}

const AdminServiceRequestList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const srTableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const dateRangePickerRef = useRef(null);
  const statusSelectRef = useRef(null);
  const { sidebarOpen } = useSidebar();

  // Date range is aligned with /csrp/approver-dashboard (default: current financial year)
  const getCurrentFinancialYearRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0 = Jan, 3 = Apr
    const fyStartYear = month < 3 ? year - 1 : year;
    const fyEndYear = month < 3 ? year : year + 1;
    const start = `${fyStartYear}-04-01`;
    const end = `${fyEndYear}-03-31`;
    const fmt = (y, m, d) => {
      const dd = String(d).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      return `${dd}-${mm}-${y}`;
    };
    const displayText = `${fmt(fyStartYear, 4, 1)} - ${fmt(fyEndYear, 3, 31)}`;
    return { start, end, displayText };
  };

  const defaultFY = getCurrentFinancialYearRange();
  const savedRestoreSnapshot = readAdminServiceRequestListRestoreSnapshot();
  const drilldownFromUrl = hasAdminServiceRequestListDrilldownUrl(location.search);
  const savedStateForInit =
    savedRestoreSnapshot && !drilldownFromUrl ? savedRestoreSnapshot : null;
  const initialUrlFilters = parseAdminListUrlFilters(location.search, defaultFY);
  const initialMarketCode = initialUrlFilters.appliedFilters.marketCode
    ? initialUrlFilters.appliedFilters.marketCode
    : savedStateForInit?.appliedFilters
      ? savedStateForInit.appliedFilters.marketCode || null
      : DEFAULT_ADMIN_LIST_MARKET;
  const initialMarketFilter = initialUrlFilters.ui.marketCode
    ? initialUrlFilters.ui.marketCode
    : savedStateForInit?.appliedFilters
      ? savedStateForInit.appliedFilters.marketCode || "0000"
      : DEFAULT_ADMIN_LIST_MARKET;
  const initialAppliedFilters = savedStateForInit?.appliedFilters
    ? { ...savedStateForInit.appliedFilters }
    : {
        ...initialUrlFilters.appliedFilters,
        marketCode: initialMarketCode,
      };
  const repeatScopeRef = useRef({
    from: initialUrlFilters.appliedFilters.repeatComplaintsScopeFromDate,
    to: initialUrlFilters.appliedFilters.repeatComplaintsScopeToDate,
    drilldown: initialUrlFilters.appliedFilters.repeatComplaintsDrilldown,
  });
  const loadSeqRef = useRef(0);
  const restoreAppliedRef = useRef(Boolean(savedRestoreSnapshot));
  const listLocationRef = useRef({
    pathname: location.pathname,
    search: location.search,
  });
  const statusFilterRef = useRef([]);
  const categoryFilterRef = useRef("");
  const productVerticalFilterRef = useRef([]);
  const raisedByFilterRef = useRef("");
  const srInvoiceFilterRef = useRef("");
  const startDateRef = useRef(initialUrlFilters.ui.startDate);
  const endDateRef = useRef(initialUrlFilters.ui.endDate);
  const appliedFiltersRef = useRef(initialAppliedFilters);
  const drilldownBvCsvRef = useRef(
    initialUrlFilters.appliedFilters.businessVerticalCodesCsv || null
  );
  const bvOptionsRef = useRef([]);

  // Filter states
  const [statusFilter, setStatusFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(
    initialUrlFilters.ui.categoryFilter || ""
  );
  const [marketFilter, setMarketFilter] = useState(initialMarketFilter);
  const [srTypeCodeFilter, setSrTypeCodeFilter] = useState("");
  const [productVerticalFilter, setProductVerticalFilter] = useState(
    initialUrlFilters.ui.productVerticalFilter || []
  );
  const [raisedByFilter, setRaisedByFilter] = useState("");
  const [srInvoiceFilter, setSrInvoiceFilter] = useState(
    initialUrlFilters.ui.srInvoiceFilter || ""
  );

  const [startDate, setStartDate] = useState(initialUrlFilters.ui.startDate);
  const [endDate, setEndDate] = useState(initialUrlFilters.ui.endDate);
  const [dateRangeText, setDateRangeText] = useState("");
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(true);

  // When navigation passes dashboard status tokens (e.g. ?status=2), we map them
  // to the SP's expected StatusName strings ('Pending'/'Approved'/'Rejected')
  // and also select the matching dropdown item.
  const [backendStatusForUrl, setBackendStatusForUrl] = useState(
    initialUrlFilters.backendStatusForUrl
  );

  // Logged-in user (used for secure createdBy filtering on this page)
  const [currentUserIdCode, setCurrentUserIdCode] = useState(null);

  // Applied filters for server-side API; set when user clicks Filter (createdBy only when queryRaised mode)
  const [appliedFilters, setAppliedFilters] = useState(() => initialAppliedFilters);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [statusSelectSyncTick] = useState(0);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  useEffect(() => {
    statusFilterRef.current = statusFilter;
    categoryFilterRef.current = categoryFilter;
    productVerticalFilterRef.current = productVerticalFilter;
    raisedByFilterRef.current = raisedByFilter;
    srInvoiceFilterRef.current = srInvoiceFilter;
    startDateRef.current = startDate;
    endDateRef.current = endDate;
    appliedFiltersRef.current = appliedFilters;
  }, [
    statusFilter,
    categoryFilter,
    productVerticalFilter,
    raisedByFilter,
    srInvoiceFilter,
    startDate,
    endDate,
    appliedFilters,
  ]);

  useEffect(() => {
    listLocationRef.current = {
      pathname: location.pathname,
      search: location.search,
    };
  }, [location.pathname, location.search]);

  const applySavedListSnapshot = (saved) => {
    if (!saved) return;

    if (Array.isArray(saved.statusFilter)) {
      setStatusFilter(saved.statusFilter);
    }
    if (saved.categoryFilter != null) {
      setCategoryFilter(String(saved.categoryFilter || ""));
    }
    if (Array.isArray(saved.productVerticalFilter)) {
      setProductVerticalFilter(saved.productVerticalFilter);
    }
    if (saved.appliedFilters?.businessVerticalCodesCsv) {
      drilldownBvCsvRef.current = saved.appliedFilters.businessVerticalCodesCsv;
    } else if (saved.drilldownBvCsv) {
      drilldownBvCsvRef.current = saved.drilldownBvCsv;
    }
    if (saved.raisedByFilter != null) {
      setRaisedByFilter(String(saved.raisedByFilter || ""));
    }
    if (saved.srInvoiceFilter != null) {
      setSrInvoiceFilter(String(saved.srInvoiceFilter || ""));
    }
    if (saved.startDate) setStartDate(saved.startDate);
    if (saved.endDate) setEndDate(saved.endDate);
    if (saved.startDate && saved.endDate) {
      const displayText = `${moment(saved.startDate, "YYYY-MM-DD").format("DD-MM-YYYY")} - ${moment(
        saved.endDate,
        "YYYY-MM-DD"
      ).format("DD-MM-YYYY")}`;
      setDateRangeText(displayText);
      if (dateRangePickerRef.current) {
        $(dateRangePickerRef.current).val(displayText);
      }
    }
    if (saved.appliedFilters) {
      setAppliedFilters(saved.appliedFilters);
      setMarketFilter(saved.appliedFilters.marketCode || "0000");
      repeatScopeRef.current = {
        from: saved.appliedFilters.repeatComplaintsScopeFromDate || null,
        to: saved.appliedFilters.repeatComplaintsScopeToDate || null,
        drilldown: Boolean(saved.appliedFilters.repeatComplaintsDrilldown),
      };
    }
    if (
      (Array.isArray(saved.statusFilter) && saved.statusFilter.length > 0) ||
      (Array.isArray(saved.productVerticalFilter) && saved.productVerticalFilter.length > 0) ||
      saved.categoryFilter ||
      saved.srInvoiceFilter
    ) {
      setIsFilterCollapsed(false);
    }
    restoreAppliedRef.current = true;
  };

  const [delegateOpen, setDelegateOpen] = useState(false);
  const [delegateRow, setDelegateRow] = useState(null);
  const [delegateUserId, setDelegateUserId] = useState("");
  const [delegateComments, setDelegateComments] = useState("");
  const [delegateUsers, setDelegateUsers] = useState([]);
  const [delegateUsersLoading, setDelegateUsersLoading] = useState(false);
  const [delegateSaving, setDelegateSaving] = useState(false);
  const [delegateError, setDelegateError] = useState(null);

  // Master data for filter dropdowns (from APIs)
  const [statusOptionsFromApi, setStatusOptionsFromApi] = useState([]); // from sp_GetSRStatusList (Greenlam.SRSTATUSMASTER)
  const [bvOptions, setBvOptions] = useState([]);
  const [userList, setUserList] = useState([]); // from user master
  const [marketOptions, setMarketOptions] = useState([]); // from master markets
  const [srTypeOptions, setSrTypeOptions] = useState([]); // from Greenlam.SRTYPES (by market)

  // Helper functions reused from Dashboard presets
  const getFinancialYearStart = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    if (month < 3) {
      // Jan-Mar: FY starts previous year April
      return new Date(year - 1, 3, 1);
    }
    return new Date(year, 3, 1);
  };

  const getQuarterStart = (date) => {
    const month = date.getMonth();
    const quarter = Math.floor(month / 3);
    return new Date(date.getFullYear(), quarter * 3, 1);
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  const getUniqueValues = (values = []) =>
    Array.from(
      new Set(values.filter((item) => item !== undefined && item !== null && item !== ""))
    );

  // Status options: from sp_GetSRStatusList (Greenlam.SRSTATUSMASTER), populated in useEffect
  const statusOptions = statusOptionsFromApi;

  // Raised By / Territory options: from user master (userList), populated in useEffect
  const raisedByOptionsFromMaster = (userList || []).map((u) => {
    const name =
      u.fullName ||
      u.FullName ||
      u.displayName ||
      u.DisplayName ||
      u.userName ||
      u.UserName ||
      u.userIdCode ||
      u.UserIdCode ||
      "";
    return {
      id: u.userIdCode || u.UserIdCode || "",
      text: displayNameOnly(name),
    };
  });

  // SR Category options now come from Greenlam.SRTYPES (srTypeOptions) filtered by Market.

  // Product/Vertical and Raised By options come from master APIs (bvOptions, userList).

  const raisedByOptionsFromData = getUniqueValues(
    allData.map(
      (item) =>
        item.createdByName ||
        item.CreatedByName ||
        item.raisedBy ||
        item.RaisedBy ||
        item.territory ||
        item.Territory ||
        ""
    )
  );
  const raisedByOptions =
    raisedByOptionsFromMaster.length > 0
      ? raisedByOptionsFromMaster
      : raisedByOptionsFromData.map((text) => ({
          id: text,
          text: displayNameOnly(text),
        }));

  const srInvoiceSuggestions = getUniqueValues(
    allData
      .flatMap((item) => {
        const sr =
          item.srNumber ||
          item.SRNumber ||
          item.complaintNumber ||
          item.ComplaintNumber ||
          item.ComplaintNo ||
          item.complaintNo ||
          item.Id ||
          item.id ||
          "";
        const invoice =
          item.invoiceNumber ||
          item.InvoiceNumber ||
          item.invoiceNo ||
          item.InvoiceNo ||
          "";
        return [sr, invoice]
          .map((val) => (val == null ? "" : String(val).trim()))
          .filter((val) => val);
      })
  );

  // Load master data for filter dropdowns: SR status, users, markets
  useEffect(() => {
    let mounted = true;

    const loadMasters = async () => {
      try {
        const [statusRes, usersRes, marketsRes] = await Promise.all([
          complaintAPI.getSRStatuses(),
          userAPI.getSeByTerritory({ approverUserIdCode: currentUserIdCode, status: "active" }),
          masterDataAPI.getMarkets("", 1, 200),
        ]);

        if (!mounted) return;

        if (statusRes?.success && Array.isArray(statusRes.data)) {
          setStatusOptionsFromApi(
            statusRes.data.map((x) => ({
              id: x.code || x.SRStatusCODE || "",
              text: x.name || x.NAME || x.code || "",
            }))
          );
        }

        const users =
          usersRes?.success && Array.isArray(usersRes?.data)
            ? usersRes.data
            : Array.isArray(usersRes)
              ? usersRes
              : [];
        if (Array.isArray(users) && users.length > 0) {
          setUserList(users);
        }

        // Markets (normalize multiple response shapes)
        const mPayload = marketsRes?.data ?? marketsRes?.Data ?? marketsRes;
        const mList =
          (mPayload?.data && Array.isArray(mPayload.data) ? mPayload.data : null) ||
          (Array.isArray(mPayload) ? mPayload : null) ||
          (mPayload?.markets && Array.isArray(mPayload.markets) ? mPayload.markets : null) ||
          [];
        const normalizedMarkets = (Array.isArray(mList) ? mList : []).map((m) => ({
          marketCode: (m.marketCode || m.MarketCode || m.MARKETCODE || m.code || m.Code || "").toString().trim(),
          marketName: (m.marketName || m.MarketName || m.MARKETNAME || m.name || m.Name || "").toString().trim(),
        }));
        const markets = normalizedMarkets.filter(
          (x) => x.marketCode && x.marketCode !== "0000"
        );
        setMarketOptions(markets);
        if (mounted && !initialUrlFilters.appliedFilters.marketCode && !savedStateForInit) {
          const defaultMarket = pickDefaultListMarket(markets);
          setMarketFilter((prev) =>
            !prev || prev === "0000" ? defaultMarket : prev
          );
          setAppliedFilters((prev) =>
            prev.marketCode ? prev : { ...prev, marketCode: defaultMarket }
          );
        }
      } catch (err) {
        console.error("Failed to load filter masters", err);
      }
    };

    loadMasters();
    return () => { mounted = false; };
  }, []);

  // Load Business Vertical options (CATEGORYMASTER) by selected Market
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        if (!marketFilter || marketFilter === "0000") {
          if (mounted) {
            setBvOptions([]);
            if (!drilldownBvCsvRef.current) {
              setProductVerticalFilter([]);
            }
          }
          return;
        }
        const response = await productMasterAPI.getCategories(true, marketFilter);
        const list =
          (response?.success && Array.isArray(response?.data) ? response.data : null) ||
          (response?.data?.data && Array.isArray(response.data.data) ? response.data.data : null) ||
          (response?.Data && Array.isArray(response.Data) ? response.Data : null) ||
          [];
        const mapped = (Array.isArray(list) ? list : [])
          .map((c) => ({
            id: (c.productCategoryId || c.productcategoryId || c.catgCode || c.CatgCode || "").toString().trim(),
            text: (
              c.productCategoryDescription ||
              c.productcategoryDescription ||
              c.catgName ||
              c.CatgName ||
              c.productCategoryId ||
              c.productcategoryId ||
              c.catgCode ||
              c.CatgCode ||
              ""
            ).toString().trim(),
          }))
          .filter((o) => o.id && o.text);
        console.log("[AdminSR] Business Vertical by Market", {
          marketCode: marketFilter,
          totalFromApi: Array.isArray(list) ? list.length : 0,
          totalAfterNormalize: mapped.length,
          options: mapped,
        });
        if (mounted) {
          setBvOptions(mapped);
          // Keep only valid selected values for current market
          setProductVerticalFilter((prev) =>
            (Array.isArray(prev) ? prev : []).filter((x) => mapped.some((m) => m.id === x))
          );
        }
      } catch (err) {
        console.error("Failed to load Business Vertical by Market", err);
        if (mounted) setBvOptions([]);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [marketFilter]);

  // Load SR Types for selected Market (SR Category dropdown)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        if (!marketFilter || marketFilter === "0000") {
          console.log("[AdminSR] SR Category load skipped: market not selected", {
            marketFilter,
          });
          if (mounted) setSrTypeOptions([]);
          return;
        }
        console.log("[AdminSR] Loading SR Category by Market", {
          marketCode: marketFilter,
        });
        const res = await masterDataAPI.getSRTypes(marketFilter, true);
        console.log("[AdminSR] SR Types API raw response", res);
        const payload = res?.data ?? res?.Data ?? res;
        const list =
          (Array.isArray(payload) ? payload : null) ||
          (Array.isArray(payload?.data) ? payload.data : null) ||
          (Array.isArray(payload?.srTypes) ? payload.srTypes : null) ||
          [];
        const normalized = (Array.isArray(list) ? list : []).map((x) => ({
          srTypeCode: (x.srTypeCode || x.SRTypeCode || x.SRTYPECODE || "").toString().trim(),
          name: (x.name || x.Name || x.NAME || "").toString().trim(),
          description: (x.description || x.Description || x.DESCRIPTION || "").toString().trim(),
        }));
        console.log("[AdminSR] SR Types normalized", {
          marketCode: marketFilter,
          totalFromApi: list.length,
          totalAfterNormalize: normalized.filter((x) => x.srTypeCode).length,
          options: normalized,
        });
        if (mounted) setSrTypeOptions(normalized.filter((x) => x.srTypeCode));
      } catch (e) {
        console.error("Failed to load SR types", e);
        if (mounted) setSrTypeOptions([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, [marketFilter]);

  // Resolve current user's UserIdCode once on mount (prevents accidental broad queries)
  useEffect(() => {
    const u = getUser();
    const id =
      (u?.userIdCode || u?.UserIdCode || u?.USERIDCODE || "").toString().trim();
    setCurrentUserIdCode(id || null);
  }, []);

  // When "queries raised by me" is active, bind createdBy to the logged-in user; otherwise admin list is global (null).
  useEffect(() => {
    if (!currentUserIdCode) return;

    try {
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
    } catch (e) {
      // ignore
    }
    setAllData([]);
    setFilteredData([]);
    setTotalRecords(0);
    setPageNumber(1);

    setAppliedFilters((prev) => ({
      ...prev,
      createdBy: prev.onlyQueriesRaisedByMe ? currentUserIdCode : null,
    }));

    if (process.env.NODE_ENV === "development") {
      console.log("[Admin SR List] currentUserIdCode:", currentUserIdCode);
    }
  }, [currentUserIdCode]);

  useEffect(() => {
    bvOptionsRef.current = bvOptions;
  }, [bvOptions]);

  // Restore saved list filters when returning from SR detail; otherwise read URL query params.
  useEffect(() => {
    const drilldownFromUrl = hasAdminServiceRequestListDrilldownUrl(location.search);
    const shouldRestore =
      !drilldownFromUrl &&
      (Boolean(location.state?.restoreAdminServiceRequestList) ||
        Boolean(readAdminServiceRequestListRestoreSnapshot()));

    if (shouldRestore) {
      const saved = readAdminServiceRequestListState();
      applySavedListSnapshot(saved);
      return;
    }

    restoreAppliedRef.current = false;
    const parsed = parseAdminListUrlFilters(location.search, defaultFY);

    if (parsed.appliedFilters.businessVerticalCodesCsv) {
      drilldownBvCsvRef.current = parsed.appliedFilters.businessVerticalCodesCsv;
    } else {
      drilldownBvCsvRef.current = null;
    }

    repeatScopeRef.current = {
      from: parsed.appliedFilters.repeatComplaintsScopeFromDate,
      to: parsed.appliedFilters.repeatComplaintsScopeToDate,
      drilldown: parsed.appliedFilters.repeatComplaintsDrilldown,
    };

    setBackendStatusForUrl(parsed.backendStatusForUrl || null);
    if (parsed.backendStatusForUrl) {
      setStatusFilter([]);
    }

    setStartDate(parsed.ui.startDate);
    setEndDate(parsed.ui.endDate);
    if (dateRangePickerRef.current && window.$) {
      const displayText = `${window.moment(parsed.ui.startDate, "YYYY-MM-DD").format("DD-MM-YYYY")} - ${window.moment(parsed.ui.endDate, "YYYY-MM-DD").format("DD-MM-YYYY")}`;
      window.$(dateRangePickerRef.current).val(displayText);
    }

    if (parsed.ui.productVerticalFilter?.length) {
      setProductVerticalFilter(parsed.ui.productVerticalFilter);
      setIsFilterCollapsed(false);
    }
    if (parsed.ui.srInvoiceFilter) {
      setSrInvoiceFilter(parsed.ui.srInvoiceFilter);
    }
    if (parsed.ui.categoryFilter) {
      setCategoryFilter(parsed.ui.categoryFilter);
    }
    const resolvedMarketCode =
      parsed.appliedFilters.marketCode || DEFAULT_ADMIN_LIST_MARKET;

    if (parsed.ui.marketCode) {
      setMarketFilter(parsed.ui.marketCode);
      setIsFilterCollapsed(false);
    } else {
      setMarketFilter(resolvedMarketCode);
    }

    setAppliedFilters({
      createdBy: null,
      statusCode: parsed.appliedFilters.statusCode || null,
      statusCodesCsv: parsed.appliedFilters.statusCodesCsv || null,
      businessVerticalCodesCsv: parsed.appliedFilters.businessVerticalCodesCsv || null,
      marketCode: resolvedMarketCode,
      srTypeCode: null,
      category: parsed.appliedFilters.category || null,
      settlementType: parsed.appliedFilters.settlementType || null,
      fromDate: parsed.appliedFilters.fromDate || null,
      toDate: parsed.appliedFilters.toDate || null,
      repeatComplaintsScopeFromDate:
        parsed.appliedFilters.repeatComplaintsScopeFromDate || null,
      repeatComplaintsScopeToDate:
        parsed.appliedFilters.repeatComplaintsScopeToDate || null,
      repeatComplaintsDrilldown: parsed.appliedFilters.repeatComplaintsDrilldown || false,
      raisedByUserIdCode: null,
      searchText: parsed.appliedFilters.searchText || null,
      onlyQueriesRaisedByMe: parsed.appliedFilters.onlyQueriesRaisedByMe || false,
      zoneName: parsed.appliedFilters.zoneName || null,
    });
    setListRefreshKey((k) => k + 1);
  }, [location.search, location.state?.restoreAdminServiceRequestList, defaultFY.start, defaultFY.end]);

  // After status options load, select the matching dropdown item for the mapped backend status.
  useEffect(() => {
    if (!backendStatusForUrl) return;
    if (!Array.isArray(statusOptionsFromApi) || statusOptionsFromApi.length === 0) return;

    const match = statusOptionsFromApi.find((o) => {
      const text = (o.text || "").toString().trim().toLowerCase();
      return text === backendStatusForUrl.toLowerCase();
    });

    if (match?.id) {
      setStatusFilter([match.id]);
    }
  }, [backendStatusForUrl, statusOptionsFromApi.length]);

  useEffect(() => {
    if (!delegateOpen || !delegateRow?.srCode) return undefined;
    let cancelled = false;
    (async () => {
      setDelegateUsersLoading(true);
      setDelegateError(null);
      setDelegateUsers([]);
      try {
        const parent = String(delegateRow.srCode || "").trim();
        const lineNo = String(delegateRow.srNumber || "").trim();
        const childSrCode =
          lineNo && lineNo.toLowerCase() !== parent.toLowerCase() ? lineNo : null;
        const changedBy =
          currentUserIdCode ||
          (getUser()?.userIdCode || getUser()?.UserIdCode || "").toString().trim() ||
          undefined;

        const res = await complaintAPI.getServiceRequestHeader({
          parentSrCode: parent,
          childSrCode: childSrCode || undefined,
          changedBy,
        });
        const payload = res?.data ?? res?.Data ?? {};
        const journeyRows =
          payload.journeyStatus ?? payload.JourneyStatus ?? [];
        const users = mapJourneyRowsToDelegateUsers(journeyRows);
        if (!cancelled) {
          setDelegateUsers(users);
          if (users.length === 0) {
            setDelegateError("No pending workflow users found for this SR.");
          }
        }
      } catch (err) {
        console.error("Failed to load SR journey users for delegation", err);
        if (!cancelled) {
          setDelegateUsers([]);
          setDelegateError("Could not load SR journey users. Try again.");
        }
      } finally {
        if (!cancelled) setDelegateUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [delegateOpen, delegateRow, currentUserIdCode]);

  const handleDelegateSave = useCallback(async () => {
    if (!delegateRow?.srCode) {
      setDelegateError("Missing service request.");
      return;
    }
    if (!delegateUserId) {
      setDelegateError("Select a user to assign.");
      return;
    }
    const changedBy =
      currentUserIdCode ||
      (getUser()?.userIdCode || getUser()?.UserIdCode || "").toString().trim();
    if (!changedBy) {
      setDelegateError("Logged-in user is required (UserIdCode).");
      return;
    }
    const parent = String(delegateRow.srCode || "").trim();
    const lineNo = String(delegateRow.srNumber || "").trim();
    const childSRNumber =
      lineNo && lineNo.toLowerCase() !== parent.toLowerCase() ? lineNo : undefined;

    setDelegateSaving(true);
    setDelegateError(null);
    try {
      const res = await complaintAPI.delegateServiceRequestAssignment(
        {
          srCode: parent,
          childSRNumber,
          delegateToUserIdCode: delegateUserId.trim(),
          comments: delegateComments?.trim() || undefined,
        },
        changedBy
      );
      if (res?.success) {
        setDelegateOpen(false);
        setDelegateRow(null);
        setDelegateUserId("");
        setDelegateComments("");
        setListRefreshKey((k) => k + 1);
      } else {
        setDelegateError(res?.message || "Assignment failed.");
      }
    } catch (err) {
      const body = err?.response?.data;
      const msg =
        body?.message ||
        body?.Message ||
        (typeof body === "string" ? body : null) ||
        err?.message ||
        "Assignment failed.";
      setDelegateError(msg);
    } finally {
      setDelegateSaving(false);
    }
  }, [
    delegateRow,
    delegateUserId,
    delegateComments,
    currentUserIdCode,
  ]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const effectiveCreatedBy =
        (appliedFilters.createdBy || currentUserIdCode || "")
          .toString()
          .trim();
      if (appliedFilters.onlyQueriesRaisedByMe && !effectiveCreatedBy) return;

      const repeatScopeFrom =
        appliedFilters.repeatComplaintsScopeFromDate ||
        repeatScopeRef.current.from ||
        null;
      const repeatScopeTo =
        appliedFilters.repeatComplaintsScopeToDate ||
        repeatScopeRef.current.to ||
        null;
      const repeatDrilldown =
        appliedFilters.repeatComplaintsDrilldown ||
        repeatScopeRef.current.drilldown ||
        Boolean(repeatScopeFrom || repeatScopeTo);

      const loadSeq = ++loadSeqRef.current;
      setLoading(true);
      try {
        const urlParsed = parseAdminListUrlFilters(location.search, defaultFY);
        const effectiveFromDate =
          appliedFilters.fromDate || urlParsed.appliedFilters.fromDate || undefined;
        const effectiveToDate =
          appliedFilters.toDate || urlParsed.appliedFilters.toDate || undefined;
        const effectiveZoneName =
          appliedFilters.zoneName || urlParsed.appliedFilters.zoneName || undefined;
        const effectiveMarketCode =
          appliedFilters.marketCode || urlParsed.appliedFilters.marketCode || undefined;

        const urlBvCsv = parseBusinessVerticalCsvFromSearch(location.search);
        const rawBvCsv =
          appliedFilters.businessVerticalCodesCsv ||
          drilldownBvCsvRef.current ||
          urlBvCsv ||
          null;
        const bvMatch = buildBusinessVerticalMatchContext(
          rawBvCsv,
          null,
          bvOptionsRef.current
        );
        const businessVerticalCodesCsv =
          bvMatch.codesCsv || rawBvCsv || undefined;

        const listParams = {
          statusCode: appliedFilters.statusCode || undefined,
          statusCodesCsv: appliedFilters.statusCodesCsv || undefined,
          businessVerticalCodesCsv,
          marketCode: effectiveMarketCode,
          srTypeCode: appliedFilters.srTypeCode || undefined,
          settlementType: appliedFilters.settlementType || undefined,
          fromDate: effectiveFromDate,
          toDate: effectiveToDate,
          zoneName: effectiveZoneName,
          repeatComplaintsScopeFromDate: repeatScopeFrom || undefined,
          repeatComplaintsScopeToDate: repeatScopeTo || undefined,
          repeatComplaintsDrilldown: repeatDrilldown || undefined,
          raisedByUserIdCode: appliedFilters.raisedByUserIdCode || undefined,
          searchText: appliedFilters.searchText || undefined,
          queryRaised: appliedFilters.onlyQueriesRaisedByMe || undefined,
          pageNumber: 1,
          pageSize: 500,
        };
        if (appliedFilters.onlyQueriesRaisedByMe) {
          listParams.createdBy = effectiveCreatedBy;
        }

        if (process.env.NODE_ENV === "development" && repeatDrilldown) {
          console.log("[Admin SR List] Repeat-complaints drilldown API params:", {
            fromDate: listParams.fromDate,
            toDate: listParams.toDate,
            repeatComplaintsScopeFromDate: listParams.repeatComplaintsScopeFromDate,
            repeatComplaintsScopeToDate: listParams.repeatComplaintsScopeToDate,
            repeatComplaintsDrilldown: listParams.repeatComplaintsDrilldown,
          });
        }

        const res = await complaintAPI.getServiceRequestListAdmin(listParams);

        if (!mounted || loadSeq !== loadSeqRef.current) return;

        const rawItems =
          res?.success && res?.data?.items && Array.isArray(res.data.items)
            ? res.data.items
            : [];
        const total = res?.data?.totalRecords ?? 0;
        if (!mounted) return;

        // Map sp_GetServiceRequestList response to table row shape (no extra fields)
        const mapped = rawItems.map((d) => {
          const srCode = d.srCode || d.SRCode || "";
          const srNo = d.srNo || d.SRNo || d.srNumber || d.SRNumber || srCode;
          const status = d.currentStatus || d.CurrentStatus || d.status || d.Status || "";
          const raisedBy = d.raisedBy || d.RaisedBy || "";
          const raisedByUserIdCode =
            d.raisedByUserIdCode ||
            d.RaisedByUserIdCode ||
            d.createdBy ||
            d.CreatedBy ||
            "";
          // Match BCM list: prefer API AssignedTo; else "FullName (Role)".
          const assignedTo = d.assignedTo || d.AssignedTo || "";
          const assignToRoleId = d.assignToRoleId || d.AssignToRoleId || "";
          const assignToUserTypeCode = d.assignToUserTypeCode || d.AssignToUserTypeCode || "";
          const assignToUserFullName = d.assignToUserFullName || d.AssignToUserFullName || "";
          const assignToUserName = d.assignToUserName || d.AssignToUserName || "";
          const assignedToName =
            (assignedTo && String(assignedTo).trim()) ||
            (assignToUserFullName && assignToRoleId
              ? `${assignToUserFullName} (${assignToRoleId})`
              : assignToUserFullName || assignToUserName || "");
          const srCategory = d.srCategory || d.SRCategory || "";
          const category = d.category || d.Category || "";
          const srTypeCode = d.srTypeCode || d.SRTypeCode || d.SRTYPECODE || "";
          const settlementType =
            d.cnType ||
            d.CnType ||
            d.CNTYPE ||
            d.settlementType ||
            d.SettlementType ||
            "";
          return {
            id: srCode,
            srCode,
            srNumber: srNo,
            statusName: status,
            statusCode: status,
            createdByName: raisedBy,
            raisedByUserIdCode,
            assignedToName,
            complaintTypeName: srCategory,
            productCategory: category,
            srTypeCode,
            settlementType,
            invoiceNumber: d.invoiceNumber || d.InvoiceNumber || "",
            createdDate: d.srDate || d.SRDate || d.invoiceDate || d.InvoiceDate,
            assignToRoleId,
            assignToUserTypeCode,
            assignToUserFullName,
            assignToUserName,
            slNo: d.slNo ?? d.SLNo,
          };
        });

        // Client-side filtering for fields not yet handled server-side
        let items = mapped;

        // Market is filtered server-side via @MarketCode in sp_GetServiceRequestList_Admin.
        // SR Category (SRType): exact match on srTypeCode
        if (appliedFilters.srTypeCode) {
          const sel = String(appliedFilters.srTypeCode).trim().toLowerCase();
          items = items.filter((item) => {
            const code = String(item.srTypeCode || "").trim().toLowerCase();
            if (code) return code === sel;
            // Fallback: match by SR Category name when code is absent in payload
            const selectedType = (srTypeOptions || []).find(
              (x) => String(x.srTypeCode || "").trim().toLowerCase() === sel
            );
            const wantedName = String(selectedType?.name || "").trim().toLowerCase();
            if (!wantedName) return true;
            return String(item.complaintTypeName || "").trim().toLowerCase() === wantedName;
          });
        }

        // Category drilldown (e.g. ?category=Quality from admin dashboard charts).
        // Match against SR Category text first, fallback to product category text.
        if (appliedFilters.category) {
          const wanted = String(appliedFilters.category).trim().toLowerCase();
          items = items.filter((item) => {
            const srCat = String(item.complaintTypeName || "").trim().toLowerCase();
            const prodCat = String(item.productCategory || "").trim().toLowerCase();
            return srCat === wanted || prodCat === wanted;
          });
        }

        // Settlement type drilldown (dashboard StoCnValue / legacy CnType).
        // Server filters via @SettlementType in sp_GetServiceRequestList_Admin.
        // Defensive client filter when rows expose settlementType/cnType/stoCnValue.
        if (appliedFilters.settlementType) {
          const wanted = String(appliedFilters.settlementType).trim().toLowerCase();
          const settlementOf = (item) =>
            String(
              item.settlementType ||
                item.SettlementType ||
                item.cnType ||
                item.CnType ||
                item.stoCnValue ||
                item.StoCnValue ||
                ""
            )
              .trim()
              .toLowerCase();
          const anyHasSettlement = items.some((it) => settlementOf(it) !== "");
          if (anyHasSettlement) {
            items = items.filter((item) => {
              const st = settlementOf(item);
              return !st || st === wanted;
            });
          }
        }

        if (appliedFilters.raisedByUserIdCode) {
          const sel = String(appliedFilters.raisedByUserIdCode).trim().toLowerCase();
          // Prefer filtering by RaisedByUserIdCode when API provides it.
          const hasCode = items.some((it) => String(it.raisedByUserIdCode || "").trim());
          if (hasCode) {
            items = items.filter(
              (item) => String(item.raisedByUserIdCode || "").trim().toLowerCase() === sel
            );
          } else {
            // Fallback: filter by displayed RaisedBy text (e.g. "Full Name ( Role )").
            // This keeps the filter usable even when backend hasn't yet been deployed with RaisedByUserIdCode.
            const u = (userList || []).find((x) => {
              const id = (x.userIdCode || x.UserIdCode || "").toString().trim().toLowerCase();
              return id === sel;
            });
            const name =
              (u?.fullName ||
                u?.FullName ||
                u?.displayName ||
                u?.DisplayName ||
                u?.userName ||
                u?.UserName ||
                "")
                .toString()
                .trim()
                .toLowerCase();
            if (name) {
              items = items.filter((item) => {
                const rb = String(item.createdByName || "").toLowerCase();
                // Match "name (" prefix or exact equals.
                return rb === name || rb.startsWith(`${name} (`) || rb.startsWith(name + " (");
              });
            }
          }
        }
        setAllData(items);
        setFilteredData(items);
        setTotalRecords(total);

        if (!$.fn.DataTable) {
          DataTable($);
        }

        if (dataTableInstanceRef.current) {
          dataTableInstanceRef.current.destroy();
          dataTableInstanceRef.current = null;
        }

        dataTableInstanceRef.current = $(srTableRef.current).DataTable({
          data: items,
          paging: true,
          searching: true,
          info: true,
          lengthChange: true,
          lengthMenu: [10, 25, 50, 100],
          pageLength: 10,
          dom: "lfrtip",
          ordering: true,
          order: [[1, "desc"]], // SR Date DESC by default
          destroy: true,
          scrollX: true,
          autoWidth: false,
          columns: [
            {
              title: "SR NO",
              data: null,
              render: (d) => {
                const parentSrCode = d.srCode || d.id || "";
                const childSrCode = d.srNumber || d.srNo || "";
                const num = childSrCode || parentSrCode;
                return renderSrLinkHtml({
                  parentSrCode,
                  childSrCode,
                  label: num,
                });
              },
              className: "text-center",
              createdCell: (td) => $(td).addClass("text-center"),
            },
            {
              title: "SR Date",
              data: "createdDate",
              render: (date, type) => {
                if (!date) return type === "sort" || type === "type" ? 0 : "-";
                const m = moment(date);
                if (!m.isValid()) return type === "sort" || type === "type" ? 0 : "-";
                if (type === "sort" || type === "type") return m.valueOf();
                return m.format("DD-MM-YYYY");
              },
              className: "text-center",
            },
            {
              title: "INVOICE NO",
              data: "invoiceNumber",
              render: (v) => v || "-",
              className: "text-center",
            },
            {
              title: "BUSINESS VERTICAL",
              data: "productCategory",
              render: (v) => v || "",
            },
            {
              title: "SR CATEGORY",
              data: "complaintTypeName",
              render: (v) => v || "",
            },
            {
              title: "RAISED BY",
              data: "createdByName",
              render: (v) => v || "",
            },
            {
              title: "Assigned To / Last Actioned By",
              data: null,
              // Same as BCM: prefer mapped AssignedTo / "Name (Role)" fallback
              render: (d) => {
                if (d.assignedToName) return d.assignedToName;
                const code = d.assignToRoleId || "";
                const name = d.assignToUserFullName || d.assignToUserName || "";
                if (name && code) return `${name} (${code})`;
                if (name) return name;
                if (code) return code;
                return "-";
              },
            },
            {
              title: "DELEGATE",
              data: null,
              orderable: false,
              searchable: false,
              className: "text-center",
              render: (d) => {
                const normalizedStatus = String(d?.statusName || "").trim().toLowerCase();
                const isDisabled = normalizedStatus === "closed" || normalizedStatus === "wip";
                return (
                  `<button type="button" class="btn-delegate-icon btn-delegate-sr${isDisabled ? " disabled" : ""}" title="${isDisabled ? "Delegate disabled for current status" : "Delegate"}" aria-label="Delegate"${isDisabled ? " disabled aria-disabled=\"true\"" : ""}>` +
                  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">' +
                  '<path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.98 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>' +
                  '</svg>' +
                  "</button>"
                );
              },
            },
            {
              title: "STATUS",
              data: "statusName",
              render: (status) => renderStatusHtml(status),
            },
          ],
          initComplete: function (settings, json) {
            try {
              // Wrap dt-length and dt-search into a single top filter wrapper
              const wrapper = $(srTableRef.current).closest(
                ".dataTables_wrapper, .dt-container"
              );
              if (wrapper.length > 0) {
                const lengthEl = wrapper.find(".dt-length");
                const searchEl = wrapper.find(".dt-search");
                if (lengthEl.length > 0 || searchEl.length > 0) {
                  let topWrap = wrapper.find(".dtFltrTop");
                  if (topWrap.length === 0) {
                    topWrap = $(
                      "<div class='dtFltrTop d-flex flex-wrap'></div>"
                    );
                    wrapper.prepend(topWrap);
                  }
                  lengthEl.each(function () {
                    $(this).appendTo(topWrap);
                  });
                  searchEl.each(function () {
                    $(this).appendTo(topWrap);
                  });
                }

                // Wrap dt-info and dt-paging into a single bottom filter wrapper
                const infoEl = wrapper.find(".dt-info");
                const pagingEl = wrapper.find(".dt-paging");
                if (infoEl.length > 0 || pagingEl.length > 0) {
                  let bottomWrap = wrapper.find(".dtFltrBotm");
                  if (bottomWrap.length === 0) {
                    bottomWrap = $(
                      "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                    );
                    wrapper.append(bottomWrap);
                  }
                  infoEl.each(function () {
                    $(this).appendTo(bottomWrap);
                  });
                  pagingEl.each(function () {
                    $(this).appendTo(bottomWrap);
                  });
                }
              }

              // Wrap dt-scroll with an inner wrapper and ensure outer wrapper has dtScrlWrpr
              const dtScrollDiv = $(srTableRef.current).closest(".dt-scroll");
              if (dtScrollDiv.length > 0) {
                if (!dtScrollDiv.parent().hasClass("dtScrlWrprInner")) {
                  dtScrollDiv.wrap('<div class="dtScrlWrprInner"></div>');
                }
                const outer = dtScrollDiv.parent().parent();
                if (outer.length > 0) outer.addClass("dtScrlWrpr");
              } else {
                const container = $(srTableRef.current).closest(
                  ".table-responsive"
                );
                if (container.length > 0) {
                  const dtScroll = container.find(".dt-scroll");
                  if (dtScroll.length > 0) {
                    dtScroll.each(function () {
                      const $this = $(this);
                      if (!$this.parent().hasClass("dtScrlWrprInner")) {
                        $this.wrap('<div class="dtScrlWrprInner"></div>');
                      }
                      const outer = $this.parent().parent();
                      if (outer.length > 0) outer.addClass("dtScrlWrpr");
                    });
                  }
                }
              }
            } catch (error) {
              console.error("Error in initComplete:", error);
            }
          },
        });

        // click handler for SR link (open SE view: ViewServiceRequest)
        // Pass parentSrCode (d.srCode) and childSrCode (d.srNumber) to detail page
        $(srTableRef.current).off("click", ".sr-link");
        $(srTableRef.current).on("click", ".sr-link", function (e) {
          e.preventDefault();
          const parentSrCode = $(this).data("parentSrCode");
          const childSrCode = $(this).data("childSrCode");
          if (!parentSrCode && !childSrCode) return;
          const parent = parentSrCode || childSrCode;
          const childQuery = childSrCode ? `childSrCode=${encodeURIComponent(childSrCode)}` : "";
          const fromListQuery = `fromList=${encodeURIComponent(ADMIN_SERVICE_REQUESTS_FROM_LIST)}`;
          const query = childQuery
            ? `?${childQuery}&${fromListQuery}`
            : `?${fromListQuery}`;
          saveAdminServiceRequestListState({
            pendingRestore: true,
            drilldownBvCsv: drilldownBvCsvRef.current,
            statusFilter: Array.isArray(statusFilterRef.current)
              ? [...statusFilterRef.current]
              : [],
            categoryFilter: categoryFilterRef.current,
            productVerticalFilter: Array.isArray(productVerticalFilterRef.current)
              ? [...productVerticalFilterRef.current]
              : [],
            raisedByFilter: raisedByFilterRef.current,
            srInvoiceFilter: srInvoiceFilterRef.current || "",
            startDate: startDateRef.current,
            endDate: endDateRef.current,
            appliedFilters: { ...appliedFiltersRef.current },
            returnPath: `${listLocationRef.current.pathname || "/admin-service-requests"}${
              listLocationRef.current.search || ""
            }`,
          });
          navigate(`/complaints/${encodeURIComponent(parent)}${query}`);
        });

        $(srTableRef.current).off("click", ".btn-delegate-sr");
        $(srTableRef.current).on("click", ".btn-delegate-sr", function (e) {
          const isDisabled = $(this).is(":disabled") || $(this).attr("aria-disabled") === "true";
          if (isDisabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          const api = dataTableInstanceRef.current;
          if (!api) return;
          const tr = $(this).closest("tr");
          const rowData = api.row(tr).data();
          if (!rowData) return;
          const normalizedStatus = String(rowData.statusName || "").trim().toLowerCase();
          if (normalizedStatus === "closed" || normalizedStatus === "wip") return;
          setDelegateRow(rowData);
          setDelegateUserId("");
          setDelegateComments("");
          setDelegateError(null);
          setDelegateOpen(true);
        });
      } catch (err) {
        console.error("Failed to load SRs", err);
      } finally {
        if (mounted && loadSeq === loadSeqRef.current) {
          setLoading(false);
          if (restoreAppliedRef.current) {
            markAdminServiceRequestListRestored();
            restoreAppliedRef.current = false;
          }
        }
      }
    };

    load();

    return () => {
      mounted = false;
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
      if (srTableRef.current) {
        $(srTableRef.current).off("click", ".sr-link");
        $(srTableRef.current).off("click", ".btn-delegate-sr");
      }
    };
  }, [appliedFilters, navigate, productVerticalFilter, bvOptions, listRefreshKey, location.search]);

  // Apply filters: send to API (sp_GetServiceRequestList) and reload first page
  const handleFilter = () => {
    // Normalize selected status values to backend status names (Pending/Closed/WIP/etc.)
    // because some status codes do not map consistently in admin list SP filtering.
    const selectedStatusValues = Array.isArray(statusFilter) ? statusFilter : [];
    const selectedStatusNames = selectedStatusValues
      .map((selected) => {
        const selectedText = String(selected ?? "").trim();
        if (!selectedText) return "";
        const match = (statusOptions || []).find((opt) => {
          const id = String(opt?.id ?? "").trim().toLowerCase();
          const text = String(opt?.text ?? "").trim().toLowerCase();
          const wanted = selectedText.toLowerCase();
          return id === wanted || text === wanted;
        });
        return String(match?.text ?? selectedText).trim();
      })
      .filter(Boolean);

    const statusCsv =
      selectedStatusNames.length > 0 ? selectedStatusNames.join(",") : null;
    const bvCsv =
      Array.isArray(productVerticalFilter) && productVerticalFilter.length > 0
        ? productVerticalFilter
            .map((v) => String(v ?? "").trim())
            .filter(Boolean)
            .join(",")
        : null;
    drilldownBvCsvRef.current = bvCsv;
    const mktCode = marketFilter && marketFilter !== "0000" ? marketFilter.trim() : null;
    const srType = srTypeCodeFilter && srTypeCodeFilter.trim() ? srTypeCodeFilter.trim() : null;

    setAppliedFilters((prev) => ({
      createdBy: prev.onlyQueriesRaisedByMe ? currentUserIdCode : null,
      statusCode:
        selectedStatusNames.length > 0 ? (selectedStatusNames[0] || null) : null,
      statusCodesCsv: statusCsv,
      businessVerticalCodesCsv: bvCsv,
      marketCode: mktCode,
      srTypeCode: srType,
      category: categoryFilter && categoryFilter.trim() ? categoryFilter.trim() : null,
      settlementType: prev.settlementType || null,
      // Preserve repeat-complaint scope across user-driven Filter clicks so that
      // narrowing the visible date range still keeps the drilldown intent.
      repeatComplaintsScopeFromDate: prev.repeatComplaintsScopeFromDate || null,
      repeatComplaintsScopeToDate: prev.repeatComplaintsScopeToDate || null,
      repeatComplaintsDrilldown: prev.repeatComplaintsDrilldown || false,
      fromDate: startDate && startDate.trim() ? startDate.trim() : null,
      toDate: endDate && endDate.trim() ? endDate.trim() : null,
      zoneName: prev.zoneName || null,
      raisedByUserIdCode: raisedByFilter && raisedByFilter.trim() ? raisedByFilter.trim() : null,
      searchText:
        srInvoiceFilter && srInvoiceFilter.trim() ? srInvoiceFilter.trim() : null,
      onlyQueriesRaisedByMe: prev.onlyQueriesRaisedByMe,
    }));
    setPageNumber(1);
    // Force list refresh even if applied filter object is unchanged.
    setListRefreshKey((v) => v + 1);
  };

  const handleReset = () => {
    setStatusFilter([]);
    setCategoryFilter("");
    setMarketFilter(DEFAULT_ADMIN_LIST_MARKET);
    setSrTypeCodeFilter("");
    setProductVerticalFilter([]);
    drilldownBvCsvRef.current = null;
    setRaisedByFilter("");
    setSrInvoiceFilter("");
    const fy = getCurrentFinancialYearRange();
    const defStart = fy.start;
    const defEnd = fy.end;
    setStartDate(defStart);
    setEndDate(defEnd);
    setDateRangeText(fy.displayText);
    setAppliedFilters({
      createdBy: null,
      statusCode: null,
      statusCodesCsv: null,
      businessVerticalCodesCsv: null,
      marketCode: DEFAULT_ADMIN_LIST_MARKET,
      srTypeCode: null,
      category: null,
      settlementType: null,
      repeatComplaintsScopeFromDate: null,
      repeatComplaintsScopeToDate: null,
      repeatComplaintsDrilldown: false,
      fromDate: defStart,
      toDate: defEnd,
      raisedByUserIdCode: null,
      searchText: null,
      onlyQueriesRaisedByMe: false,
      zoneName: null,
    });
    repeatScopeRef.current = { from: null, to: null, drilldown: false };
    setPageNumber(1);
    setListRefreshKey((v) => v + 1);

    if (dateRangePickerRef.current) {
      $(dateRangePickerRef.current).val(fy.displayText);
    }
    if (statusSelectRef.current) {
      $(statusSelectRef.current).val(null).trigger("change");
    }
    [".raised-by-select"].forEach((selector) => {
      const $el = $(selector);
      if ($el.length > 0 && $el.hasClass("select2-hidden-accessible")) {
        $el.val("").trigger("change.select2");
      }
    });
  };

  // Initialize daterangepicker (only when filter panel is expanded — input not in DOM when collapsed)
  useEffect(() => {
    if (isFilterCollapsed || !dateRangePickerRef.current) return;

    const timer = setTimeout(() => {
      const $input = $(dateRangePickerRef.current);
      if (!$input.length) return;

    const initDateRangePicker = () => {
      // Destroy existing instance if any
      if ($input.data("daterangepicker")) {
        $input.data("daterangepicker").remove();
        $input.off("apply.daterangepicker");
        $input.off("cancel.daterangepicker");
      }

      const today = moment();
      const yesterday = moment().subtract(1, "days");
      const thisWeekStart = getWeekStart(new Date());
      const lastWeekStart = moment(thisWeekStart).subtract(7, "days");
      const lastWeekEnd = moment(thisWeekStart).subtract(1, "days");
      const thisMonthStart = moment().startOf("month");
      const lastMonthStart = moment().subtract(1, "month").startOf("month");
      const lastMonthEnd = moment().subtract(1, "month").endOf("month");
      const thisQuarterStart = getQuarterStart(new Date());
      const lastQuarterStart = moment(thisQuarterStart).subtract(3, "months");
      const lastQuarterEnd = moment(thisQuarterStart).subtract(1, "days");
      const thisFYStart = getFinancialYearStart(new Date());
      const thisFYEnd = moment(thisFYStart).add(1, "year").subtract(1, "day"); // 31-Mar of same FY
      const lastFYStart = moment(thisFYStart).subtract(1, "year");
      const lastFYEnd = moment(thisFYStart).subtract(1, "days");

      try {
        $input.daterangepicker({
          autoUpdateInput: false,
          locale: {
            cancelLabel: "Clear",
            applyLabel: "Apply",
            format: "DD-MM-YYYY",
          },
          opens: "left",
          drops: "down",
          showDropdowns: true,
          ranges: {
            Today: [today, today],
            Yesterday: [yesterday, yesterday],
            "This Week": [moment(thisWeekStart), today],
            "Last Week": [lastWeekStart, lastWeekEnd],
            "This Month": [thisMonthStart, today],
            "Last Month": [lastMonthStart, lastMonthEnd],
            "This Quarter": [moment(thisQuarterStart), today],
            "Last Quarter": [lastQuarterStart, lastQuarterEnd],
            "This Financial Year": [moment(thisFYStart), thisFYEnd],
            "Last Financial Year": [lastFYStart, lastFYEnd],
          },
        });

        $input.on("apply.daterangepicker", function (ev, picker) {
          const start = picker.startDate.format("YYYY-MM-DD");
          const end = picker.endDate.format("YYYY-MM-DD");
          const displayText =
            picker.startDate.format("DD-MM-YYYY") +
            " - " +
            picker.endDate.format("DD-MM-YYYY");

          setStartDate(start);
          setEndDate(end);
          setDateRangeText(displayText);
          $(this).val(displayText);
        });

        $input.on("cancel.daterangepicker", function (ev, picker) {
          setStartDate("");
          setEndDate("");
          setDateRangeText("");
          $(this).val("");
        });

        // Default date range: current Financial Year when not already set from URL/restore
        if (!startDate && !endDate) {
          const fyStart = moment(thisFYStart);
          const fyEnd = moment(thisFYEnd);
          const defaultDisplay =
            fyStart.format("DD-MM-YYYY") + " - " + fyEnd.format("DD-MM-YYYY");
          setStartDate(fyStart.format("YYYY-MM-DD"));
          setEndDate(fyEnd.format("YYYY-MM-DD"));
          setDateRangeText(defaultDisplay);
          $input.val(defaultDisplay);
        }
      } catch (error) {
        console.error("Error initializing daterangepicker:", error);
      }
    };

    initDateRangePicker();

    const fyStart = moment(getFinancialYearStart(new Date()));
    const fyEnd = moment(fyStart).add(1, "year").subtract(1, "day");
    const fyDisplayText =
      fyStart.format("DD-MM-YYYY") + " - " + fyEnd.format("DD-MM-YYYY");
    const displayText =
      dateRangeText ||
      (startDate && endDate
        ? `${moment(startDate, "YYYY-MM-DD").format("DD-MM-YYYY")} - ${moment(endDate, "YYYY-MM-DD").format("DD-MM-YYYY")}`
        : fyDisplayText);
    $input.val(displayText);

    const handleInteraction = () => {
      if (!$input.data("daterangepicker")) {
        initDateRangePicker();
      }
    };

    $input.on("click.daterangepicker-reinit", handleInteraction);
    $input.on("focus.daterangepicker-reinit", handleInteraction);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (dateRangePickerRef.current) {
        const $input = $(dateRangePickerRef.current);
        if ($input.data("daterangepicker")) {
          $input.off("apply.daterangepicker");
          $input.off("cancel.daterangepicker");
          $input.off("click.daterangepicker-reinit");
          $input.off("focus.daterangepicker-reinit");
          $input.data("daterangepicker").remove();
        }
      }
    };
  }, [isFilterCollapsed, dateRangeText, startDate, endDate]);

  // Status is rendered using CustomMultiSelect (same design as /csrp/admin-dashboard).
  // The hidden <select> (statusSelectRef) is kept only for compatibility with reset/DOM logic.

  // Initialize select2 for other filter dropdowns (re-run when master lists load)
  useEffect(() => {
    if (isFilterCollapsed) return;

    const selectors = [
      {
        selector: ".raised-by-select",
        placeholder: "Raised By",
      },
    ];

    selectors.forEach(({ selector, placeholder }) => {
      const $el = $(selector);
      if ($el.length === 0) return;
      if ($el.hasClass("select2-hidden-accessible")) {
        $el.select2("destroy");
      }
      $el.select2({
        placeholder,
        allowClear: true,
        width: "100%",
      });

      // IMPORTANT: Select2 mutates the DOM; bind change handlers directly so React state stays in sync.
      if (selector === ".raised-by-select") {
        $el.off("change.adminFilters").on("change.adminFilters", function (e) {
          setRaisedByFilter(e?.target?.value || "");
        });
      }
    });

    return () => {
      selectors.forEach(({ selector }) => {
        const $el = $(selector);
        $el.off("change.adminFilters");
        if ($el.length > 0 && $el.hasClass("select2-hidden-accessible")) {
          $el.select2("destroy");
        }
      });
    };
  }, [isFilterCollapsed, allData.length, userList.length]);

  // Sync select2 values with state (after reset or external changes)
  useEffect(() => {
    // Sync status multiselect
    if (statusSelectRef.current) {
      const $select = $(statusSelectRef.current);
      if ($select.length > 0 && $select.hasClass("select2-hidden-accessible")) {
        const currentVal = $select.val() || [];
        const newVal = statusFilter || [];
        // Compare arrays
        const currentStr = JSON.stringify(currentVal.sort());
        const newStr = JSON.stringify(newVal.sort());
        if (currentStr !== newStr) {
          $select.val(newVal).trigger("change");
        }
      }
    }

    // Sync other dropdowns
    const syncValue = (selector, value) => {
      const $el = $(selector);
      if ($el.length > 0 && $el.hasClass("select2-hidden-accessible")) {
        const currentVal = $el.val() || "";
        const newVal = value || "";
        if (currentVal !== newVal) {
          $el.val(newVal).trigger("change.select2");
        }
      }
    };

    syncValue(".raised-by-select", raisedByFilter);
  }, [statusFilter, srTypeCodeFilter, raisedByFilter, statusOptionsFromApi.length]);

  // Handle sidebar toggle - recalculate DataTable columns
  useEffect(() => {
    if (dataTableInstanceRef.current) {
      // Wait for CSS transition to complete (300ms as defined in App.js)
      const timer = setTimeout(() => {
        try {
          dataTableInstanceRef.current.columns.adjust().draw(false);
        } catch (error) {
          console.error("Error adjusting DataTable columns:", error);
        }
      }, 350); // 350ms to ensure transition completes

      return () => clearTimeout(timer);
    }
  }, [sidebarOpen]);

  const isRepeatComplaintDrilldown = Boolean(
    appliedFilters.repeatComplaintsDrilldown ||
      appliedFilters.repeatComplaintsScopeFromDate ||
      appliedFilters.repeatComplaintsScopeToDate
  );

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          className="service-request-heading"
          sx={{ color: "#333333", fontWeight: 600 }}
        >
          Admin Service Request List
        </Typography>
        {hasUserType("SE") && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/raise-complaint")}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Raise SR
          </Button>
        )}
      </Box>

      {isRepeatComplaintDrilldown && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing repeat complaints for{" "}
          {moment(appliedFilters.fromDate, "YYYY-MM-DD").format("MMM YYYY")}
          {" "}(invoices with multiple SR codes raised between{" "}
          {moment(appliedFilters.repeatComplaintsScopeFromDate, "YYYY-MM-DD").format("DD-MM-YYYY")}
          {" "}and{" "}
          {moment(appliedFilters.repeatComplaintsScopeToDate, "YYYY-MM-DD").format("DD-MM-YYYY")}
          ).
        </Alert>
      )}

      {/* Filter Panel */}
      <div
        className="card-panel mb-3 filter-panel admin-sr-filter-panel"
        style={{ paddingBottom: isFilterCollapsed ? "2px" : "15px" }}
      >
        <div
          className="section-heading d-flex align-items-center gap-2 mb-3"
          onClick={() => setIsFilterCollapsed((prev) => !prev)}
          role="button"
          tabIndex={0}
          aria-expanded={!isFilterCollapsed}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsFilterCollapsed((prev) => !prev);
            }
          }}
          style={{ marginBottom: "14px", cursor: "pointer" }}
        >
          <FilterListIcon
            fontSize="small"
            className="text-muted"
            style={{
              width: "18px",
              height: "18px",
              flexShrink: 0,
              marginTop: "2px",
            }}
          />
          <span>Filter Service Requests</span>
          {isFilterCollapsed ? (
            <ExpandMoreIcon
              fontSize="small"
              className="text-muted"
              style={{
                width: "18px",
                height: "18px",
                flexShrink: 0,
                marginLeft: "auto",
              }}
            />
          ) : (
            <ExpandLessIcon
              fontSize="small"
              className="text-muted"
              style={{
                width: "18px",
                height: "18px",
                flexShrink: 0,
                marginLeft: "auto",
              }}
            />
          )}
        </div>

        {!isFilterCollapsed && (
        <div className="container-fluid">
          <div className="row g-3 align-items-end">
            {/* Status Dropdown (Multiselect) */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Status
              </label>
              <select
                ref={statusSelectRef}
                className="form-select form-control-light filter-select text-start status-multiselect"
                style={{ height: "42px", textAlign: "left", display: "none" }}
                multiple
              >
                {statusOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.text}
                  </option>
                ))}
              </select>
              <CustomMultiSelect
                options={statusOptions}
                value={Array.isArray(statusFilter) ? statusFilter : []}
                onChange={(selected) =>
                  setStatusFilter(Array.isArray(selected) ? selected : [])
                }
                placeholder="Select Status"
                disabled={false}
              />
            </div>

            {/* Market Dropdown */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">Market</label>
              <select
                className="form-select form-control-light filter-select market-select"
                value={marketFilter}
                onChange={(e) => {
                  const next = (e.target.value || "0000").toString().trim();
                  setMarketFilter(next);
                  setSrTypeCodeFilter("");
                  setSrTypeOptions([]);
                  setProductVerticalFilter([]);
                  setBvOptions([]);
                }}
              >
                <option value="0000">Select Market</option>
                {marketOptions.map((m) => (
                  <option key={m.marketCode} value={m.marketCode}>
                    {m.marketName}
                  </option>
                ))}
              </select>
            </div>

            {/* SR Category Dropdown (from Greenlam.SRTYPES) */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">SR Category</label>
              <select
                key={`sr-cat-${marketFilter}`}
                className="form-select form-control-light filter-select sr-category-select"
                value={srTypeCodeFilter}
                onChange={(e) => setSrTypeCodeFilter(e.target.value)}
                disabled={!marketFilter || marketFilter === "0000"}
              >
                <option value="">
                  {!marketFilter || marketFilter === "0000"
                    ? "Please select Market first"
                    : "All SR Categories"}
                </option>
                {srTypeOptions.map((opt) => (
                  <option key={opt.srTypeCode} value={opt.srTypeCode}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Business Vertical Dropdown */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">Business Vertical</label>
              <CustomMultiSelect
                key={`bv-${marketFilter}`}
                options={bvOptions}
                value={productVerticalFilter || []}
                onChange={(selected) =>
                  setProductVerticalFilter(Array.isArray(selected) ? selected : [])
                }
                placeholder={
                  !marketFilter || marketFilter === "0000"
                    ? "Please select Market first"
                    : "Select Business Vertical"
                }
                disabled={!marketFilter || marketFilter === "0000"}
              />
            </div>

          </div>

          <div className="row g-3 mt-1 align-items-end">
            {/* Raised By Dropdown - from user master */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">Raised By</label>
              <select
                className="form-select form-control-light filter-select raised-by-select"
                value={raisedByFilter}
                onChange={(e) => setRaisedByFilter(e.target.value)}
              >
                <option value="">Raised By</option>
                {raisedByOptions.map((opt) => (
                  <option key={opt.id || opt} value={typeof opt === "object" ? (opt.id ?? "") : opt}>
                    {typeof opt === "object" ? (opt.text || opt.id || "") : opt}
                  </option>
                ))}
              </select>
            </div>

            {/* SR / Invoice Number — hidden per product request */}
            <div className="col-12 col-md-6 col-lg-3 d-none">
              <label className="input-label d-block mb-1 text-start">SR No. / Invoice No.</label>
              <input
                type="text"
                className="form-control form-control-light"
                placeholder="Enter SR or Invoice No."
                value={srInvoiceFilter}
                list="sr-invoice-suggestions"
                onChange={(e) => setSrInvoiceFilter(e.target.value)}
              />
              <datalist id="sr-invoice-suggestions">
                {srInvoiceSuggestions.map((opt) => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </div>

            {/* Date Range Picker */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">Select Date Range</label>
              <div
                className="input-icon filter-daterange-wrapper filter-daterangepickerWrpr"
                style={{ position: "relative" }}
              >
                <input
                  type="text"
                  ref={dateRangePickerRef}
                  className="form-control form-control-light text-start filter-daterangepicker"
                  placeholder="Select date range"
                  readOnly
                  style={{ height: "42px", textAlign: "left" }}
                />
                <CalendarTodayIcon
                  className="icon-right"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    width: "16px",
                    height: "16px",
                    color: "#6c757d",
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="col-12 col-md-12 col-lg-3">
              <label
                className="input-label d-block"
                style={{ visibility: "hidden" }}
              >
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons flex-wrap admin-sr-actions">
                <button
                  type="button"
                  className="btn btn-filter"
                  onClick={handleFilter}
                  style={{
                    background:
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 24px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <FilterListIcon fontSize="small" className="me-1" />
                  Filter
                </button>
                <button
                  type="button"
                  className="btn btn-reset-filter"
                  onClick={handleReset}
                  title="Reset"
                  style={{
                    background:
                      "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 16px",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(132, 250, 176, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(132, 250, 176, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel mt-0">
          <div className="table-responsive">
            <table
              ref={srTableRef}
              className="table table-striped table-bordered complaint-table custmTblLst"
              style={{ width: "100%" }}
            ></table>
          </div>
        </div>
      </Paper>

      <Dialog
        open={delegateOpen}
        onClose={() => !delegateSaving && setDelegateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delegate assignment</DialogTitle>
        <DialogContent dividers>
          {delegateError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDelegateError(null)}>
              {delegateError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            SR:{" "}
            <strong>
              {delegateRow?.srNumber || delegateRow?.srCode || "—"}
            </strong>
            {delegateRow?.srCode && delegateRow?.srNumber !== delegateRow?.srCode && (
              <>
                {" "}
                (parent: {delegateRow.srCode})
              </>
            )}
          </Typography>
          {delegateUsersLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <FormControl fullWidth sx={{ mb: 2 }} required>
              <InputLabel id="delegate-user-label">Assign to user</InputLabel>
              <Select
                labelId="delegate-user-label"
                label="Assign to user"
                value={delegateUserId}
                onChange={(e) => setDelegateUserId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select user</em>
                </MenuItem>
                {delegateUsers.map((u) => {
                  const id = (u.userIdCode || u.UserIdCode || "").toString();
                  const name = (
                    u.fullName ||
                    u.FullName ||
                    u.displayName ||
                    u.DisplayName ||
                    u.userName ||
                    u.UserName ||
                    id
                  ).toString();
                  const role = (u.roleName || u.RoleName || "").toString().trim();
                  const label = role ? `${name} — ${role}` : name;
                  return (
                    <MenuItem key={id || name} value={id}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Comments (optional)"
            fullWidth
            multiline
            minRows={2}
            value={delegateComments}
            onChange={(e) => setDelegateComments(e.target.value)}
            disabled={delegateSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !delegateSaving && setDelegateOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelegateSave}
            disabled={delegateSaving || delegateUsersLoading || !delegateUserId}
          >
            {delegateSaving ? <CircularProgress size={22} color="inherit" /> : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminServiceRequestList;


